import React, { useId } from 'react';
import styles from './Field.module.css';

/* ══════════════════════════════════════════════════════════════════
 * TextField / TextArea
 *
 * 라벨·입력·도움말/에러를 한 덩어리로 묶습니다. 화면마다 label 과 input 을
 * 따로 배치하면 간격과 에러 표시가 제각각이 되므로 여기서 규정합니다.
 *
 * 모든 텍스트는 props 로 받습니다 (i18n).
 * ════════════════════════════════════════════════════════════════ */

interface BaseProps {
    label?: string;
    /** 필수 표시 (*) */
    required?: boolean;
    /** 선택 항목 표기 — i18n 문구를 넘깁니다 (예: "선택") */
    optionalText?: string;
    /** 입력 아래 도움말 */
    hint?: string;
    /** 에러 문구 — 있으면 hint 대신 표시되고 테두리가 빨강 */
    error?: string;
    /** 글자수 표시 (maxLength 와 함께) */
    showCount?: boolean;
}

interface TextFieldProps
    extends BaseProps,
        Omit<React.InputHTMLAttributes<HTMLInputElement>, 'className'> {
    /** 금액·전화번호 등 자리수 정렬이 필요한 입력 */
    numeric?: boolean;
}

export const TextField = ({
    label, required, optionalText, hint, error, showCount, numeric,
    id, value, maxLength, ...rest
}: TextFieldProps) => {
    const autoId = useId();
    const inputId = id || autoId;
    const len = typeof value === 'string' ? value.length : 0;

    return (
        <div className={styles.field}>
            {label && (
                <label className={styles.label} htmlFor={inputId}>
                    {label}
                    {required && <span className={styles.required} aria-hidden>*</span>}
                    {optionalText && <span className={styles.optional}>{optionalText}</span>}
                </label>
            )}
            <input
                id={inputId}
                value={value}
                maxLength={maxLength}
                aria-invalid={!!error}
                aria-describedby={error || hint ? `${inputId}-desc` : undefined}
                className={[
                    styles.input,
                    error ? styles.inputError : '',
                    numeric ? styles.inputNumeric : '',
                ].filter(Boolean).join(' ')}
                {...rest}
            />
            {(error || hint || (showCount && maxLength)) && (
                <div className={styles.foot}>
                    <span id={`${inputId}-desc`} className={error ? styles.error : styles.hint}>
                        {error || hint}
                    </span>
                    {showCount && maxLength ? (
                        <span className={styles.count}>{len}/{maxLength}</span>
                    ) : null}
                </div>
            )}
        </div>
    );
};

interface TextAreaProps
    extends BaseProps,
        Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'> {}

export const TextArea = ({
    label, required, optionalText, hint, error, showCount,
    id, value, maxLength, ...rest
}: TextAreaProps) => {
    const autoId = useId();
    const inputId = id || autoId;
    const len = typeof value === 'string' ? value.length : 0;

    return (
        <div className={styles.field}>
            {label && (
                <label className={styles.label} htmlFor={inputId}>
                    {label}
                    {required && <span className={styles.required} aria-hidden>*</span>}
                    {optionalText && <span className={styles.optional}>{optionalText}</span>}
                </label>
            )}
            <textarea
                id={inputId}
                value={value}
                maxLength={maxLength}
                aria-invalid={!!error}
                aria-describedby={error || hint ? `${inputId}-desc` : undefined}
                className={[
                    styles.input,
                    styles.textarea,
                    error ? styles.inputError : '',
                ].filter(Boolean).join(' ')}
                {...rest}
            />
            {(error || hint || (showCount && maxLength)) && (
                <div className={styles.foot}>
                    <span id={`${inputId}-desc`} className={error ? styles.error : styles.hint}>
                        {error || hint}
                    </span>
                    {showCount && maxLength ? (
                        <span className={styles.count}>{len}/{maxLength}</span>
                    ) : null}
                </div>
            )}
        </div>
    );
};

/** 폼 필드 사이 간격을 컨테이너가 책임집니다 */
export const FieldGroup = ({ children }: { children: React.ReactNode }) => (
    <div className={styles.group}>{children}</div>
);

/* ══════════════════════════════════════════════════════════════════
 * SwitchRow — 켜고 끄는 줄
 *
 * 알림 설정처럼 "이름 + 설명 + 스위치" 가 반복되는 자리에 씁니다.
 * 켜짐은 배경색만이 아니라 손잡이 위치로도 읽히게 했습니다.
 * ════════════════════════════════════════════════════════════════ */

export const SwitchList = ({ children }: { children: React.ReactNode }) => (
    <div className={styles.switchList}>{children}</div>
);

interface SwitchRowProps {
    label: string;
    desc?: string;
    checked: boolean;
    disabled?: boolean;
    onChange: (next: boolean) => void;
}

export const SwitchRow = ({ label, desc, checked, disabled, onChange }: SwitchRowProps) => (
    <div className={disabled ? `${styles.switchRow} ${styles.switchRowOff}` : styles.switchRow}>
        <span className={styles.switchBody}>
            <span className={styles.switchLabel}>{label}</span>
            {desc ? <span className={styles.switchDesc}>{desc}</span> : null}
        </span>
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            aria-label={label}
            disabled={disabled}
            className={checked ? `${styles.switch} ${styles.switchOn}` : styles.switch}
            onClick={() => onChange(!checked)}
        >
            <span className={styles.switchKnob} />
        </button>
    </div>
);
